import { useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { CalendarMonth, Notes, Troubleshoot } from "@mui/icons-material";
import { NotesComponent } from "../../../components/notes/NotesComponent";
import { RecordContext } from "../../../components/record/Record";
import { PSY_SERVICE, EDIT_LOCK_DAYS_3, REPORTS_FIRST_SERVICE } from "../../../utils/constants";
import { differenceInDays, parseISO } from "date-fns";
import { formatDateWithTime } from "../../../utils/dateUtils";
import { useField } from "../../../api/useField";
import { NoteSession } from "../types";
import { NoteSessionSelect, TitleSelect, getAvailableSessions, renderSessionView } from "../case-management-notes/NoteInputs";
import { SESSION_STATUS_TO_EXCLUDE } from "../treatment-prescription/utils";
import { useFieldSelectOptions } from "../../../utils/useFieldSelectOptions";
import { useNoteTitleChoices } from "../case-management-notes/useNoteTitleChoices";
import { useUser } from "../../../api/useAuth";
import { canSeePsyInstrumentsResponses, canSeePsyNotes } from "./psyFeature";
import { RecordsRelated } from "../../../components/recordsRelated/RecordsRelated";
import { instrumentsResponsesOptions } from "./instrumentsResponsesOptions";
import { psyEvaluationsFieldsQuery } from "../../psy-evaluations/psyEvaluationsRoutes";
import { heightOfHeaders } from "../treatmentRoutes";

type PsicologiaView = "notes" | "evaluations";

export const PsicologiaRelated = () => {
	const [searchParams] = useSearchParams();
	const recordContext = useContext(RecordContext);
	const { data: user } = useUser();
	const treatmentId = recordContext?.item?.id;
	const treatmentPrescriberUserId = recordContext?.item?.treatment_prescription?.[0]?.user_created;

	const treatmentClientOwnerUserId = recordContext?.item?.client_owner?.user_id?.id;
	const treatmentTherapistsUserIds = (recordContext?.item?.theurapists_involved_in_treamtent ?? [])
		.map((junction: any) => junction?.technicians_id?.user_id?.id)
		.filter(Boolean);

	const reportSessionTechnicianIds = (recordContext?.item?.sessions ?? [])
		.filter((session: any) => session?.service?.id === REPORTS_FIRST_SERVICE)
		.map((session: any) => session?.technician)
		.filter(Boolean);

	const showNotes = canSeePsyNotes(user?.id, user?.role?.id, treatmentTherapistsUserIds);

	const showInsideInstrumentsList = canSeePsyInstrumentsResponses(user, treatmentPrescriberUserId, treatmentClientOwnerUserId, treatmentTherapistsUserIds, reportSessionTechnicianIds);

	const evaluationsRecordsOptions = showInsideInstrumentsList
		? instrumentsResponsesOptions
		: { ...instrumentsResponsesOptions, dataGridOptions: { ...instrumentsResponsesOptions.dataGridOptions, navigateToDetail: () => "." } };

	const defaultView = showNotes ? "notes" : "evaluations";
	const [view, setView] = useState<PsicologiaView>(defaultView);
	const [title, setTitle] = useState<string>("notes");
	const [selectedSession, setSelectedSession] = useState<number | "" | null>(
		searchParams.get("[sessionId]") ? Number(searchParams.get("[sessionId]")) : null
	);

	const { formatedField: sessionsField } = useField("notes", "sessions");
	const statusChoices = useFieldSelectOptions("sessions", "status");

	const PSY_ALLOWED_TITLES = ["notes", "comunication"];
	const allTitleChoices = useNoteTitleChoices();
	const titleChoices = allTitleChoices.filter((choice) => PSY_ALLOWED_TITLES.includes(choice.value));
	const titleHasSession = title === "notes";

	const onTitleChange = (newTitle: string) => {
		setTitle(newTitle);
		if (newTitle !== "notes") setSelectedSession(null);
	};

	const psychologySessions = (recordContext?.item?.sessions ?? [])
		.filter((session: NoteSession) => session.service?.id === PSY_SERVICE)
		.sort((a: NoteSession, b: NoteSession) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
		.reduce((acc: Array<any>, session: NoteSession) => {
			const number = !SESSION_STATUS_TO_EXCLUDE.includes(session.status) ? acc.filter((s) => s.number).length + 1 : undefined;
			return [...acc, { ...session, number }];
		}, [])
		.reverse();


	if (!treatmentId) return null;

	const buttonWidth = "230px";

	const categoryConfig = {
		isNoteLocked: (note: any) => {
			if (!note?.date_created) return false;
			return differenceInDays(new Date(), parseISO(note.date_created)) > EDIT_LOCK_DAYS_3;
		},
		validateBeforeSave: () => {
			if (!title) return false;
			return true;
		},
		onCreateNew: () => {
			setSelectedSession(null);
			setTitle("notes");
		},
		onSessionChange: (sessionId: number | null) => {
			if (sessionId === null) { setSelectedSession(null); return; }
			if (selectedSession === null) setSelectedSession(sessionId);
		},
		onItemLoaded: (item: any) => {
			setTitle(item?.title ?? "notes");
		},
		titleSelect: (currentTitle: string | null) => (
			<TitleSelect
				titleState={{ choices: titleChoices, allChoices: allTitleChoices, value: title, onChange: onTitleChange, hasSession: titleHasSession }}
				currentTitle={currentTitle}
			/>
		),
		sessionSelect: !titleHasSession ? undefined : (currentSessionId: number | null, notes: Array<any>) => {
			const value = selectedSession !== null ? selectedSession : (currentSessionId ?? "");
			const availableSessions = getAvailableSessions(psychologySessions, notes, currentSessionId);
			return <NoteSessionSelect value={value} availableSessions={availableSessions} onChange={setSelectedSession} />;
		},
		renderSessionView: renderSessionView(psychologySessions, sessionsField, statusChoices),
		renderSessionListView: (noteItem: any) => {
			const sessionId = noteItem?.sessions;
			if (!sessionId) return null;
			const session = psychologySessions.find((s: any) => s.id === sessionId);
			if (!session) return null;
			const sessionLabel = session.number ? `Sessão ${session.number}` : "Sessão";
			const titleColor = allTitleChoices.find((choice) => choice.value === noteItem?.title)?.color;
			return (
				<Box display="flex" alignItems="center" gap={1}>
					{titleColor ? <CalendarMonth sx={{ color: titleColor }} /> : <CalendarMonth color="secondary" />}
					<Typography sx={{ display: "inline", fontSize: "0.8rem" }} component="span" color="text.primary">
						{sessionLabel} - {formatDateWithTime(session.start_date)}
					</Typography>
				</Box>
			);
		},
		filter: { category: { _eq: "psychology" } },
		onCreate: {
			treatments: treatmentId,
			category: "psychology",
			title: title,
			sessions: titleHasSession ? (selectedSession || null) : null,
		},
	};

	return (
		<Box sx={{ backgroundColor: "#fff" }}>
			<Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, flex: 1, gap: 4, pt: 8, px: 4, pb: 4 }}>
				<Box sx={{ display: "flex", flex: 1, gap: 4, alignItems: "center", flexDirection: { xs: "column", sm: "row" } }}>
					{showNotes && (
						<Button
							variant={view === "notes" ? "contained" : "outlined"}
							onClick={() => setView("notes")}
							startIcon={<Notes />}
							color="secondary"
							sx={{ minWidth: { sm: buttonWidth } }}
						>
							Notas
						</Button>
					)}
						<Button
							variant={view === "evaluations" ? "contained" : "outlined"}
							onClick={() => setView("evaluations")}
							startIcon={<Troubleshoot />}
							color="secondary"
							sx={{ minWidth: { sm: buttonWidth } }}
						>
							Avaliações
						</Button>
				</Box>
			</Box>
			{view === "notes" && (
				<NotesComponent categoryConfig={categoryConfig} heightOfHeaders={386} />
			)}
			{view === "evaluations" && (
				<RecordsRelated
					entity="instruments_responses"
					fieldsToQuery={psyEvaluationsFieldsQuery}
					fieldToFilter="treatment"
					recordsOptions={evaluationsRecordsOptions}
					heightOfHeaders={heightOfHeaders}
				/>
			)}
		</Box>
	);
};
